'use client';

import React, { useEffect, useRef } from 'react';

const principles = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <path d="M14 3L17.5 10.5L26 11.5L20 17.5L21.5 26L14 22L6.5 26L8 17.5L2 11.5L10.5 10.5L14 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
    title: 'Quality',
    description: 'Committed to quality-focused healthcare solutions.',
    color: 'text-primary',
    bg: 'bg-blue-50',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <path d="M14 24C14 24 4 18 4 11C4 7.7 6.7 5 10 5C12 5 13.7 6 14 7C14.3 6 16 5 18 5C21.3 5 24 7.7 24 11C24 18 14 24 14 24Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Care',
    description: 'Focused on products that address diverse healthcare needs.',
    color: 'text-secondary',
    bg: 'bg-green-50',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <path d="M6 22L12 14L16 18L20 10L24 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M4 6H24M4 6V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Advancement',
    description: 'Continuously working toward better healthcare solutions.',
    color: 'text-primary',
    bg: 'bg-blue-50',
  },
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-20 lg:py-28 bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: Content */}
          <div className="lg:col-span-7 space-y-8">
            <div className="section-reveal stagger-1">
              <span className="section-label">About Us</span>
              <h2 className="font-display text-foreground mt-3 leading-tight" style={{ fontSize: 'clamp(1.875rem, 3.5vw, 2.75rem)' }}>
                About EWIN Lifescience
              </h2>
            </div>

            <div className="section-reveal stagger-2 space-y-5">
              <p className="text-muted-foreground text-lg leading-relaxed">
                EWIN Lifescience is a pharmaceutical healthcare company focused on providing quality medicines across multiple therapeutic areas. The company&apos;s product portfolio covers segments including anti-infectives, respiratory care, gastrointestinal care, pain and inflammation, anti-allergic care, antispasmodic therapy, liver care and probiotic/women&apos;s health support.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Our focus is on quality, healthcare accessibility and advancing better health outcomes through a diverse pharmaceutical portfolio.
              </p>
            </div>

            {/* Divider line */}
            <div className="section-reveal stagger-3 flex items-center gap-4">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">Our Principles</span>
              <div className="h-px w-12 bg-border" />
            </div>
          </div>

          {/* Right: Visual accent */}
          <div className="lg:col-span-5 hidden lg:flex items-start justify-end">
            <div className="section-reveal stagger-2 relative w-full max-w-[340px]">
              <div className="aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-blue-50 to-green-50 border border-border flex items-center justify-center">
                <svg width="240" height="240" viewBox="0 0 240 240" fill="none" aria-hidden="true">
                  {/* Cross */}
                  <rect x="100" y="30" width="40" height="180" rx="8" fill="#1B3A6B" fillOpacity="0.12"/>
                  <rect x="30" y="100" width="180" height="40" rx="8" fill="#2E8B57" fillOpacity="0.12"/>
                  {/* Circles */}
                  <circle cx="120" cy="120" r="90" stroke="#1B3A6B" strokeWidth="1.5" strokeDasharray="8 6" strokeOpacity="0.2"/>
                  <circle cx="120" cy="120" r="60" stroke="#2E8B57" strokeWidth="1" strokeOpacity="0.15"/>
                  {/* Center mark */}
                  <circle cx="120" cy="120" r="20" fill="#1B3A6B" fillOpacity="0.1" stroke="#1B3A6B" strokeWidth="2" strokeOpacity="0.3"/>
                  <path d="M110 120H130M120 110V130" stroke="#2E8B57" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="absolute -bottom-3 -right-3 bg-secondary text-secondary-foreground rounded-2xl px-5 py-3 shadow-lg">
                <div className="text-sm font-bold">Pharmaceutical</div>
                <div className="text-xs opacity-80">Healthcare Company</div>
              </div>
            </div>
          </div>
        </div>

        {/* Principle Cards */}
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {principles?.map((p, i) => (
            <div key={p?.title} className={`value-card section-reveal stagger-${i + 2}`}>
              <div className={`w-12 h-12 ${p?.bg} ${p?.color} rounded-xl flex items-center justify-center mb-4`}>
                {p?.icon}
              </div>
              <h3 className="font-display text-lg text-foreground mb-2">{p?.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{p?.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}